import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { buildHead, breadcrumbJsonLd } from "@/lib/seo";
import { publicResourcesQuery } from "@/lib/query-registry";
import samuelPhoto256 from "@/assets/samuel-noriega-256.webp";
import { setEdgeHtmlCache } from "@/lib/cache-headers";
import { SiteShell } from "@/components/site/SiteShell";
import { Reveal } from "@/components/site/Reveal";
import { ResourceCard, type EditorialResource } from "@/components/resources/ResourceEditorialPrimitives";
import { formatUpdated } from "@/lib/article-utils";

const LINKEDIN_URL = "https://www.linkedin.com/in/samuelnoriega/";
const PERSON_URL = "https://shugert.com.mx/about/samuel-noriega";
const ORGANIZATION_ID = "https://shugert.com.mx#org";

function personJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${PERSON_URL}#person`,
    name: "Samuel Noriega",
    givenName: "Samuel",
    familyName: "Noriega",
    jobTitle: "Founder, Shugert Marketing",
    description:
      "Samuel leads Shopify performance, CRO and technical optimization projects for DTC brands across the U.S. and LATAM. Shopify Partner since 2015, 750+ Shopify engagements completed.",
    image: `https://shugert.com.mx${samuelPhoto256}`,
    url: PERSON_URL,
    sameAs: [LINKEDIN_URL],
    worksFor: { "@id": ORGANIZATION_ID },
    knowsAbout: ["Shopify", "Shopify Plus", "Hydrogen", "Headless Commerce", "Core Web Vitals", "Conversion Rate Optimization", "Technical SEO", "Klaviyo", "ReCharge", "B2B Commerce", "Replatforming"],
  };
}

export const Route = createFileRoute("/about/samuel-noriega")({
  loader: async ({ context }) => {
    await setEdgeHtmlCache();
    return context.queryClient.ensureQueryData(publicResourcesQuery());
  },
  head: () =>
    buildHead({
      locale: "en",
      title: "Samuel Noriega: Founder, Shugert Marketing · Shopify Select Partner",
      description:
        "Samuel Noriega founded Shugert in 2012. Shopify Partner with 750+ Shopify engagements completed across the U.S. and LATAM.",
      path: "/about/samuel-noriega",
      altPath: "/es/nosotros/samuel-noriega",
      type: "website",
      ogImage: samuelPhoto256,
      jsonLd: [personJsonLd(), breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "About", path: "/about" }, { name: "Samuel Noriega", path: "/about/samuel-noriega" }])],
    }),
  component: AuthorPage,
});

function AuthorPage() {
  const data = Route.useLoaderData();
  const resources = ((data?.resources ?? []) as EditorialResource[]).slice(0, 6);
  return (
    <SiteShell>
      <div data-page-main="">
        <section className="border-b border-border bg-[rgba(79,70,229,0.045)]">
          <div className="mx-auto grid max-w-7xl gap-10 px-6 py-20 lg:grid-cols-[0.42fr_1fr] lg:items-center lg:py-28">
            <img src={samuelPhoto256} alt="Samuel Noriega" width={256} height={256} className="h-52 w-52 rounded-3xl object-cover ring-1 ring-border sm:h-64 sm:w-64" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#0a0a0a] dark:text-[#ccff00]">Founder · Shopify engineering</p>
              <h1 className="mt-6 font-display text-5xl font-semibold leading-[1.02] tracking-tight text-foreground sm:text-6xl">Samuel Noriega</h1>
              <p className="mt-6 max-w-3xl text-lg leading-relaxed text-muted-foreground">Founder of Shugert. Samuel works across Shopify performance, CRO, technical SEO, migrations, platform architecture and applied data systems.</p>
              <div className="mt-8 flex flex-wrap gap-3"><a href={LINKEDIN_URL} className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background">LinkedIn<ArrowRight className="h-4 w-4" /></a><Link to="/about" className="inline-flex items-center rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground">About Shugert</Link></div>
            </div>
          </div>
        </section>
        <section className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
          <Reveal><p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#0a0a0a] dark:text-[#ccff00]">Recent field notes</p><h2 className="mt-5 font-display text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">Resources written from implementation work.</h2></Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">{resources.map((resource, index) => <Reveal key={resource.id ?? resource.slug} delay={(index % 3) * 40}><ResourceCard resource={resource} locale="en" updated={resource.reviewed_at || resource.updated_at ? formatUpdated((resource.reviewed_at ?? resource.updated_at) as string, "en") : ""} readLabel="Read" /></Reveal>)}</div>
        </section>
      </div>
    </SiteShell>
  );
}
